'use client'

import classNames from 'classnames'
import { motion, type Variants } from 'motion/react'
import React from 'react'

import { availableEmoji, type EmojiName } from '@portal/news-lib/emoji'

import { openLoginModal } from '@/shared/lib/login-modal'
import { useLoggedIn } from '@/shared/providers/public-id'

import { numberFormatter } from './formatter'
import styles from './view.module.css'

type EmojiButtonProps = {
    name: EmojiName
    count: number
    onClick: (position: { x: number; y: number }) => void
    isChecked: boolean
}

export function EmojiButton(props: EmojiButtonProps) {
    return (
        <React.Suspense fallback={<EmojiButtonSkeleton name={props.name} count={props.count} />}>
            <EmojiButtonContent {...props} />
        </React.Suspense>
    )
}

function EmojiButtonSkeleton({ name, count }: { name: EmojiName; count: number }) {
    return (
        <motion.button type="button" className={styles.emojiButton} disabled layout>
            <span dangerouslySetInnerHTML={{ __html: availableEmoji[name] }} className={styles.emoji} />
            <CounterSpan count={count} />
        </motion.button>
    )
}

function EmojiButtonContent({ name, count, onClick, isChecked }: EmojiButtonProps) {
    const loggedIn = useLoggedIn()

    const handleButtonClick = (event: React.MouseEvent) => {
        const domRect = event.currentTarget.getBoundingClientRect()
        const x = domRect.left + domRect.width / 2
        const y = domRect.top - 10

        if (!loggedIn) {
            openLoginModal('reaction')
            return
        }

        onClick({ x, y })
    }

    return (
        <motion.button
            type="button"
            className={styles.emojiButton}
            whileHover="hover"
            whileTap="tap"
            onClick={handleButtonClick}
            layout
        >
            <motion.span
                variants={emojiMotion}
                dangerouslySetInnerHTML={{ __html: availableEmoji[name] }}
                className={styles.emoji}
            />
            {isChecked ? <CounterSpanChecked count={count} /> : <CounterSpan count={count} />}
        </motion.button>
    )
}

const emojiMotion: Variants = {
    hover: {
        scale: 1.3,
        cursor: 'pointer',
        translateY: -10,
    },
    tap: {
        scale: 1.3,
        translateY: -20,
    },
}

const counterMotion = {
    hover: {
        backgroundColor: '#59a892',
        color: '#fff',
    },
}

type CounterSpanProps = {
    count: number
}

function CounterSpan({ count }: CounterSpanProps) {
    return (
        <motion.span className={classNames(styles.countUnchecked)} variants={counterMotion} data-value={count}>
            {numberFormatter.format(count)}
        </motion.span>
    )
}

function CounterSpanChecked({ count }: CounterSpanProps) {
    return <motion.span className={styles.countChecked}>{numberFormatter.format(count)}</motion.span>
}
